import { Button, HeadlineText, TextField } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import { IdeasCheckmarkIcon } from "components/IdeasIcons";
import React from "react";
import { Student } from "src/pods/students";
import filterByName from "src/utils/filterByName";

type SelectStudentsProps = {
  onRequestHide: VoidFunction;
  onChange: (students: Student[]) => void;
  students: Student[];
};

type SelectStudentsState = {
  filteredStudents: Student[] | null;
  selectedStudents: Student[];
};

const initialState: SelectStudentsState = {
  filteredStudents: null,
  selectedStudents: [],
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class SelectStudents extends React.Component<SelectStudentsProps, SelectStudentsState> {
  constructor(props: SelectStudentsProps) {
    super(props);
    this.state = initialState;
  }

  render() {
    const { onRequestHide, students } = this.props;
    const { filteredStudents, selectedStudents } = this.state;

    function renderStudent(this: SelectStudents, student: Student) {
      const isSelected = selectedStudents.includes(student);
      return (
        <li sx={{ listStyle: "none" }}>
          <button
            data-name="select_students:render_student:img"
            key={student._id}
            sx={studentStyle}
            onClick={() => this.toggleStudent(student)}
            aria-pressed={isSelected}
          >
            <img sx={avatarStyle} src={student.avatar} role="presentation" />
            <HeadlineText as="h2" level={2} sx={nameStyle}>
              {autoTranslate("__firstName__ __lastLetter__.", {
                firstName: student.firstName,
                lastLetter: student.lastName.slice(0, 1),
              })}
            </HeadlineText>
            <div sx={isSelected ? checkedCheckmark : uncheckedCheckmark}>
              <IdeasCheckmarkIcon sx={checkmarkIcon} />
            </div>
          </button>
        </li>
      );
    }

    return (
      <article>
        <header sx={headerStyle}>
          <HeadlineText level={2} sx={titleStyle} as="h1">
            Choose students
          </HeadlineText>
          <Button
            size="s"
            kind="tertiary"
            data-name="select_students:render:modal_actions_done"
            sx={doneButtonStyle}
            onClick={onRequestHide}
          >
            Done
          </Button>
        </header>
        <section sx={{ padding: "l" }}>
          <TextField
            aria-label={autoTranslate("Type or select name")}
            autoFocus
            placeholder={autoTranslate("Type or select name")}
            onChange={this.filterStudents}
            sx={{ marginBottom: "l" }}
          />
          <ul sx={{ padding: RAW_cssValue("0"), margin: RAW_cssValue("0") }} aria-live="polite">
            {students && (filteredStudents || students).map(renderStudent, this)}
          </ul>
        </section>
      </article>
    );
  }

  toggleStudent = (student: Student) => {
    let { selectedStudents } = this.state;
    if (selectedStudents.includes(student)) {
      selectedStudents = selectedStudents.filter((s) => s !== student);
    } else {
      selectedStudents = [...selectedStudents, student];
    }
    this.setState({ selectedStudents }, () => this.props.onChange(selectedStudents));
  };

  filterStudents = (value: string) => {
    const { students } = this.props;
    this.setState({
      filteredStudents: value ? filterByName(students, value) : null,
    });
  };
}

const headerStyle: ThemeUIStyleObject = {
  borderBottom: "dt_divider",
  position: "relative",
};

const titleStyle: ThemeUIStyleObject = {
  paddingY: "dt_s",
  textAlign: "center",
};

const doneButtonStyle: ThemeUIStyleObject = {
  position: "absolute",
  top: "50%",
  right: "0",
  transform: "translateY(-50%)",
};

const studentStyle: ThemeUIStyleObject = {
  borderBottom: RAW_cssValue("1px solid rgba(0,0,0,0.1)"),
  padding: RAW_cssValue("10px 0"),
  textAlign: "left",
  verticalAlign: "middle",
  border: "none",
  backgroundColor: "transparent",
  width: "100%",
};

const nameStyle: ThemeUIStyleObject = {
  display: "inline-block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const avatarStyle: ThemeUIStyleObject = {
  display: "inline-block",
  maxWidth: "40px",
  maxHeight: "40px",
  margin: RAW_cssValue("0 14px 0 14px"),
  verticalAlign: "sub",
};

const checkmark: ThemeUIStyleObject = {
  display: "inline-block",
  width: "40px",
  height: "40px",
  float: "right",
  margin: RAW_cssValue("0 20px 0 0"),
  borderRadius: "dt_radius_s",
  lineHeight: "40px",
  textAlign: "center",
};

const uncheckedCheckmark: ThemeUIStyleObject = {
  ...checkmark,
  backgroundColor: RAW_cssValue("#ccc"),
};

const checkedCheckmark: ThemeUIStyleObject = {
  ...checkmark,
  backgroundColor: RAW_cssValue("rgb(0,174,239)"),
};

const checkmarkIcon: ThemeUIStyleObject = {
  display: "inline-block",
  verticalAlign: "middle",
  color: "dt_content_light",
  fontSize: "18px",
};
